import React, { useCallback, useState, useEffect } from 'react';
import _ from 'lodash';
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { MLVehicleInfoLookupService } from 'ml-services-common-react';
import { MLDriverVehicleUtil } from 'ml-portals-util-react';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react'
import { MLPortalSettingsService} from 'gw-capability-quoteandbind';
import appConfig from 'app-config';
import PropTypes from 'prop-types';
import { platformMessages } from 'ml-components-platform-react';
import metadata from './VehicleVinLookupComponent.metadata.json5'
import messages from './VehicleVinLookupComponent.messages';
import styles from './VehicleVinLookupComponent.module.scss';

function VehicleVinLookupComponent(props) {
    const { authHeader } = useAuthentication();
    const {
        vehicleVM,
        id,
        quoteID,
        isVinDuplicated,
        onValueChange,
        path,
        onValidate,
        showValidationErrors,
        updateVehicleDetails,
        isPUP,
        startupVerification,
        isGpa,
        updateIsValidVin,
        updateInValidVin,
        removeInValidVin,
        index,
        isModernProductPersonalAuto,
        shouldShowProgressDialog
    } = props;
    const { onValidate: setComponentValidation, isComponentValid } = useValidation(id);
    const translator = useTranslator();


    const [skipVINValidation, setskipVINValidation] = useState(false);
    const [vinError, setVinError] = useState();
    const [validVin, setValidVin] = useState(false);
    const [duplicatedVin, setDuplicatedVin] = useState(false);
    const [currentYear] = useState(_.get(vehicleVM, 'year.value'));
    const [currentMake] = useState(_.get(vehicleVM, 'make.value'));
    const [currentModel] = useState(_.get(vehicleVM, 'model.value'));
    const [currentTrim] = useState(_.get(vehicleVM, 'trim_ML.value'));
    const isOfVehicleType = (category) => _.get(vehicleVM, "type.value.code", "") === category;
    const isPrivatePassenger = isOfVehicleType('private_passenger_autos_ml');
    const isCollectible = isOfVehicleType('collectible_ml');
    const isLightTruck = isOfVehicleType('pickups_ml');
    const isVanSuv = isOfVehicleType('vans_suvs_ml');
    const isPanelTruck = isOfVehicleType('panel_trucks_ml');
    const isFlatbedTruck = isOfVehicleType('flat_bed_box_truck_ml');

    const successLookupMessage = '0';
    const isQB = appConfig.portalCode === "QB"

    const {
        showProgressDialog,
        hideProgressDialog
    } = MLModalService();

    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, onValidate, isComponentValid]);

    const isVehicleYearPre1981 = useCallback(() => {
        const vehicleYearValue = _.get(vehicleVM, 'year.value');
        return vehicleYearValue && vehicleYearValue < 1981;
    }, [vehicleVM]);

    const hasValidFullVin = useCallback(() => {
        const vin = _.get(vehicleVM, 'vin.value');
        return !_.isNil(vin) && vin.length === 17
    }, [vehicleVM]);

    const hasVin = useCallback(() => {
        return !_.isUndefined(_.get(vehicleVM, 'vin.value'))
    }, [vehicleVM, isVehicleYearPre1981]);

    const isVinValid = useCallback((vinIntegrationValid) => {
        const vin = _.get(vehicleVM, 'vin');

        if (skipVINValidation) {
            if (removeInValidVin && (isVehicleYearPre1981() ? hasVin() : hasValidFullVin()) && vin?.value !== "") {
                removeInValidVin(index);
                
                return true;
            } else if (updateInValidVin) {
                updateInValidVin(index);
            }
        }

        return vin.aspects.valid && vinIntegrationValid && !isVinDuplicated(vehicleVM) && (isVehicleYearPre1981() ? hasVin() : hasValidFullVin());
    }, [skipVINValidation, vehicleVM, isVinDuplicated, isVehicleYearPre1981, hasVin, hasValidFullVin]);

    const setVehicleVariables = useCallback((vinIntegrationValid = true) => {
        const vinValue = _.get(vehicleVM, 'vin.value');
        const vinValidFlag = isVinValid(vinIntegrationValid) && !_.isNil(vinValue);

        setValidVin(vinValidFlag);

        if (updateIsValidVin) {
            updateIsValidVin(vinValidFlag);
        }

        if (!isQB || vinValue !== null) {
            setDuplicatedVin(isVinDuplicated(vehicleVM));
        }

        return vinValidFlag;
    }, [isVinDuplicated, isVinValid, updateIsValidVin, vehicleVM]);

    const showVerifyButton = useCallback(() => {
        if (skipVINValidation) {
            return false;
        }
 
        return !isVehicleYearPre1981();
    }, [isVehicleYearPre1981, skipVINValidation]);

    /*
        Used to verify pre-1981 vehicles when the user edits the VIN. The vin verification service doesn't get called.
        For all the other vehicles the verification happens when the verify button is clicked or during startup.
    */
    const updatePre1981Details = useCallback(() => {
        if (isVehicleYearPre1981()) {
            setVehicleVariables();
        }
    }, [isVehicleYearPre1981, setVehicleVariables]);

    const {
        mapVinResponseToVehicle,
        getErrorDescription,
        hasErrorCodes,
        createLookupCompCollRequest
    } = MLDriverVehicleUtil();

    const lookupCollision = async (req, retryAttempt = 0) => {
        if (retryAttempt === 0 && shouldShowProgressDialog) {
            showProgressDialog(platformMessages.loading);
        }

        try {
            const vehicleInfo = await MLVehicleInfoLookupService.lookUpCollisionCompSymbol(req, authHeader);

            if (vehicleInfo.comprehensiveSymbol_ML && vehicleInfo.collisionSymbol_ML) {
                _.set(vehicleVM, "comprehensiveSymbol_ML.value", vehicleInfo.comprehensiveSymbol_ML);
                _.set(vehicleVM, "collisionSymbol_ML.value", vehicleInfo.collisionSymbol_ML);
                _.set(vehicleVM, "hasSymbolsFilledByCostNew_Ext.value", true);
            }

            return vehicleInfo;
        } catch (error) {
            const CDCE = error.baseError && error.baseError.indexOf('ConcurrentDataChangeException') > -1;
            // minimal: 2 reties max
            if (CDCE && retryAttempt < 2) {
                await new Promise(resolve => setTimeout(resolve, 200 * (retryAttempt + 1)))
                return await lookupCollision(req, retryAttempt + 1);
            }
            throw error;
        } finally {
            if (retryAttempt === 0 && shouldShowProgressDialog) {
                hideProgressDialog();
            }
        }
    };

    const onVehicleVinLookup = useCallback(async (fromInit) => {
        const vehicleVinValue = _.get(vehicleVM, 'vin.value');
        const isVinVerified = _.get(vehicleVM, 'isVinVerifiedSuccessfully_ML.value', false);

        if (vehicleVinValue && vehicleVinValue.length > 0 && !isVinVerified) {
            const req = {
                vin: vehicleVinValue,
                quoteID: quoteID.value
            };

            const vehicleInfoResult = await MLVehicleInfoLookupService.verifyVin(req, authHeader);
            const vinIntegrationValid = !hasErrorCodes(vehicleInfoResult);
            const vinValidFlag = setVehicleVariables(vinIntegrationValid);

            setVinError(translator(messages.vinResponseInvalidPleaseTryAgain));

            if (!vinValidFlag && updateInValidVin) {
                updateInValidVin(index);
            } else if (removeInValidVin) {
                removeInValidVin(index)
            }

            if (!vinValidFlag) {
                _.set(vehicleVM, 'year.value', currentYear);
                _.set(vehicleVM, 'make.value', currentMake);
                _.set(vehicleVM, 'model.value', currentModel);
                _.set(vehicleVM, 'trim_ML.value', currentTrim);
            } else {
                const category = _.get(vehicleVM, "category_ML.value.code");

                mapVinResponseToVehicle(vehicleInfoResult, vehicleVM.value);

                if ((_.isUndefined(vehicleInfoResult?.collSymbol) ||
                    _.isEmpty(vehicleInfoResult?.collSymbol) ||
                    _.isUndefined(vehicleInfoResult?.compSymbol) ||
                    _.isEmpty(vehicleInfoResult?.compSymbol))
                    && vehicleInfoResult?.msrp?.amount !== 0 && vehicleInfoResult?.msrp?.amount !== null && !_.isUndefined(vehicleInfoResult?.msrp?.amount)) {

                    const compCollReq = createLookupCompCollRequest(
                        vehicleInfoResult.year,
                        vehicleInfoResult.make.code,
                        vehicleInfoResult.model.code,
                        quoteID.value,
                        vehicleInfoResult.vehicleTypeCode,
                        category,
                        vehicleInfoResult.collSymbol,
                        vehicleInfoResult.compSymbol,
                        vehicleInfoResult.msrp,
                        vehicleInfoResult.trim,
                    );

                    await lookupCollision(compCollReq);
                } 
                
               const shouldShowCostNew = vehicleInfoResult?.msrp?.amount === 0 && 
               ((_.isEmpty(vehicleInfoResult?.collSymbol) && 
               _.isEmpty(vehicleInfoResult?.compSymbol)) || 
               (vehicleInfoResult?.collSymbol === null &&
               vehicleInfoResult?.compSymbol === null) ||
               (vehicleInfoResult?.collSymbol.isUndefined&&
               vehicleInfoResult?.compSymbol.isUndefined));
             
               _.set(vehicleVM, 'costNew.value.amount', vehicleInfoResult?.msrp?.amount);
               const hasLookupCostNew = Number(vehicleInfoResult?.msrp?.amount) > 0;
               props?.onCostNewRequiredChange?.(shouldShowCostNew, hasLookupCostNew);
            }

            updateVehicleDetails();

            if (!fromInit) {
                MLTagManagerService.sendLinkClickEvent(`${MLTagManagerService.getStepName()}_Lookup VIN`);
            }
        }
    }, [vehicleVM,
        quoteID.value,
        mapVinResponseToVehicle,
        setVehicleVariables,
        getErrorDescription,
        updateVehicleDetails,
        currentYear,
        currentMake,
        currentModel,
        currentTrim,
        hasErrorCodes,
        isGpa,
        authHeader,
        updateInValidVin,
        removeInValidVin
    ]);

    useEffect(() => {
      MLPortalSettingsService.getSettings(authHeader).then((response) => {
           if (response ) {
               setskipVINValidation(response.skipVinValidation_Ext && isPUP && !(isPrivatePassenger || isCollectible || isLightTruck || isVanSuv || isPanelTruck || isFlatbedTruck));
            }
        }).catch(()=>{
            console.error("Skip VIN Validation logic failed");
        }).finally (() => {
           if (startupVerification && !isVehicleYearPre1981() && hasValidFullVin && !skipVINValidation) {
            onVehicleVinLookup(true);
           }

           setVehicleVariables();
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleValueChange = useCallback((value, changedPath) => {
        onValueChange(value, `${path}.${changedPath}`);
        if (changedPath === "vin") {
            _.set(vehicleVM, 'vin.value', value);
           if (skipVINValidation) {
            setVehicleVariables(hasVin() && value !== "");
            if (value?.length > 0) {
                if(!isVehicleYearPre1981()) {
                    setValidVin(hasValidFullVin)
                }
            }
           }
        }
        updatePre1981Details();
    }, [onValueChange, path, updatePre1981Details]);

    useEffect(() => {
        const vin = _.get(vehicleVM, 'vin');
        
        if (skipVINValidation) {
            if (updateInValidVin && (!hasVin() || vin?.value === "" || !(isVehicleYearPre1981() ? hasVin() : hasValidFullVin()))) {
                updateInValidVin(index);
            } 
        }
    },[skipVINValidation]);

    const overrideProps = {
        vinError: {
            content: vinError,
            visible: !validVin && !duplicatedVin
        },
        duplicatedVin: {
            content: translator(messages.duplicatedVin),
            visible: !validVin && duplicatedVin
        },
        vinVerified: {
            visible: validVin && showVerifyButton()
        },
        lookupVinButton: {
            visible: showVerifyButton(),
            disabled: isModernProductPersonalAuto && (validVin && showVerifyButton()),
            content: isGpa ? translator(messages.verifyVin) : isQB ? translator(messages.lookupVin) : translator(messages.LOOKUPVINBtn),
            className: isQB ? 'lookupVinButton qbWidth secondaryButton widthSmall' : 'lookupVinButton'
        },
        vinNumberInput: {
            onValueChange: handleValueChange,
            disabled: validVin && !isVehicleYearPre1981() && !skipVINValidation,
            maxLength: 17
        },
        vinBottomLabel: {
            visible: !isGpa && isPUP ? _.get(vehicleVM, 'vin.aspects.valid') : true
        },
        requiredVin: {
            visible: (!validVin && !vinError && !isVehicleYearPre1981()) || (isVehicleYearPre1981() && (_.get(vehicleVM, 'vin.value') === undefined || _.get(vehicleVM, 'vin.value') === ""))
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            onLookupVin: onVehicleVinLookup
        },
        resolveClassNameMap: styles
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
            overrideProps={overrideProps}
            model={vehicleVM}
            onValidationChange={setComponentValidation}
            showErrors={showValidationErrors}
        />
    );
}

VehicleVinLookupComponent.propTypes = {
    vehicleVM: PropTypes.shape({
        value: PropTypes.shape({})
    }).isRequired,
    id: PropTypes.string.isRequired,
    isVinDuplicated: PropTypes.func.isRequired,
    onValueChange: PropTypes.func.isRequired,
    path: PropTypes.string.isRequired,
    quoteID: PropTypes.string,
    onValidate: PropTypes.func.isRequired,
    showValidationErrors: PropTypes.bool.isRequired,
    updateVehicleDetails: PropTypes.func.isRequired,
    isPUP: PropTypes.bool,
    startupVerification: PropTypes.bool,
    isGpa: PropTypes.bool,
    updateIsValidVin: PropTypes.func,
    updateInValidVin: PropTypes.func,
    removeInValidVin: PropTypes.func,
    isModernProductPersonalAuto: PropTypes.bool,
    shouldShowProgressDialog: PropTypes.bool
};

VehicleVinLookupComponent.defaultProps = {
    quoteID: undefined,
    isPUP: false,
    startupVerification: true,
    isGpa: false,
    updateIsValidVin: undefined,
    isModernProductPersonalAuto: false,
    shouldShowProgressDialog: true
};


export default VehicleVinLookupComponent;
